import { EmptyState, Page, Symbols } from '@mnd-frontend/ui/publish/components';
import Honeybadger from 'honeybadger-js';
import React, { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { track } from '../lib/tracker';

class PageWrapperErrorCatcher extends React.PureComponent<{
  setError: () => void;
  t: any;
  hasError: boolean;
  children?: React.ReactNode;
}> {
  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    track({ event: 'Native Page Error' });
    // eslint-disable-next-line no-console
    console.error(error, info);
    Honeybadger.notify(error, info);
    this.props.setError();
  }

  override render() {
    if (this.props.hasError) {
      const { t } = this.props;

      return (
        <Page>
          <EmptyState
            symbol={Symbols.AlertSign}
            headline={t('common_error_page-unexpected_title')}
            description={t('common_error_page-unexpected_desc')}
          />
        </Page>
      );
    }

    return this.props.children || null;
  }
}

const PageWrapper = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [erroredAtLocation, setErroredAtLocation] = useState<string | null>(null);
  const [t] = useTranslation();
  const clearErrorKey = erroredAtLocation && location.pathname !== erroredAtLocation;

  useEffect(() => {
    if (clearErrorKey) {
      setErroredAtLocation(null);
    }
  }, [clearErrorKey]);

  return (
    <PageWrapperErrorCatcher
      hasError={!!erroredAtLocation}
      setError={useCallback(() => {
        // Use window location to not rebuild function identity
        setErroredAtLocation(window.location.pathname);
      }, [])}
      t={t}
    >
      {children}
    </PageWrapperErrorCatcher>
  );
};

export default PageWrapper;
